/**
 * /reset-password — Cognito self-serve password-reset confirm step.
 *
 * Reads `?email=` (optional) from the URL — the /forgot-password page
 * hands it off so the user doesn't retype it. Form fields:
 *   - email (pre-filled if from query)
 *   - 6-digit code (from the email Cognito just sent)
 *   - new password + confirm password
 *
 * On submit: `confirmPassword(email, code, newPassword)` → success view
 * with a "Sign in →" link.
 *
 * Error states (mapped via describeResetPasswordError):
 *   - CodeMismatchException        — wrong code
 *   - ExpiredCodeException         — code older than 1h (Cognito default)
 *   - LimitExceededException       — too many attempts
 *   - InvalidPasswordException     — new password violates the policy
 *
 * 1:1 port of the legacy `3mote-fe/src/pages/auth/ResetPassword/`,
 * adapted for code-based confirm (vs the legacy token-link flow). The
 * legacy `TokenLinkExpired` view is collapsed into the inline error
 * banner because Cognito doesn't have a separate "verify token" step
 * before the form is shown — the code is verified on submit.
 */

import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { confirmPassword } from '@/auth/cognito'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { describeResetPasswordError } from '@/features/auth/describe-error'

interface ResetPasswordSearch {
  email?: string
}

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
})

const MIN_PASSWORD_LENGTH = 12

function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const { email: initialEmail } = Route.useSearch()
  const [email, setEmail] = useState(initialEmail ?? '')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    // Client-side validation matching the legacy form's pre-submit checks.
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError(t('resetPassword.errorInvalidCode'))
      return
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t('resetPassword.errorPasswordTooShort', { minLength: MIN_PASSWORD_LENGTH }))
      return
    }
    if (newPassword !== confirmPasswordValue) {
      setError(t('resetPassword.errorPasswordsMismatch'))
      return
    }

    setSubmitting(true)
    try {
      await confirmPassword(email, code, newPassword)
      setSuccess(true)
    } catch (err) {
      setError(describeResetPasswordError(err, t))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm rounded-lg border border-border bg-bg-elevated p-6 shadow-sm">
        {success ? (
          <>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/40">
              <svg
                aria-hidden="true"
                className="h-6 w-6 text-emerald-600 dark:text-emerald-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>{t('resetPassword.successIconTitle')}</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="mb-1 text-2xl font-semibold text-fg">
              {t('resetPassword.successTitle')}
            </h1>
            <p className="mb-6 text-sm text-fg-muted">{t('resetPassword.successMessage')}</p>
            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:opacity-90 active:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fg"
            >
              {t('resetPassword.successSignInLink')}
            </Link>
          </>
        ) : (
          <>
            <h1 className="mb-1 text-2xl font-semibold text-fg">{t('resetPassword.title')}</h1>
            <p className="mb-6 text-sm text-fg-muted">{t('resetPassword.subtitle')}</p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <FormField
                id="reset-email"
                label={t('resetPassword.emailLabel')}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <FormField
                id="reset-code"
                label={t('resetPassword.codeLabel')}
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                placeholder={t('resetPassword.codePlaceholder')}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <FormField
                id="reset-new-password"
                label={t('resetPassword.newPasswordLabel')}
                type="password"
                autoComplete="new-password"
                placeholder={t('resetPassword.newPasswordPlaceholder')}
                minLength={MIN_PASSWORD_LENGTH}
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <FormField
                id="reset-confirm-password"
                label={t('resetPassword.confirmPasswordLabel')}
                type="password"
                autoComplete="new-password"
                placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                minLength={MIN_PASSWORD_LENGTH}
                required
                value={confirmPasswordValue}
                onChange={(e) => setConfirmPasswordValue(e.target.value)}
              />

              {error && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-200">
                  {error}
                </p>
              )}

              <p className="text-xs text-fg-muted">
                {t('resetPassword.passwordRequirements', { minLength: MIN_PASSWORD_LENGTH })}
              </p>

              <Button
                type="submit"
                disabled={submitting || !email || !code || !newPassword || !confirmPasswordValue}
                className="w-full"
              >
                {submitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
              </Button>
            </form>

            <p className="mt-4 text-center text-sm">
              <Link className="text-fg-muted hover:text-fg" to="/forgot-password">
                {t('resetPassword.requestNewCode')}
              </Link>
            </p>
            <p className="mt-2 border-t border-border pt-4 text-center text-sm">
              <Link className="text-fg-muted hover:text-fg" to="/login">
                {t('resetPassword.backToSignIn')}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  )
}
